import {
  PencilIcon,
  Trash2Icon,
  ListIcon,
  GitBranchIcon,
  CopyIcon,
  ArrowLeftIcon
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from "react";

import { createMenuItem, deleteMenuItem, reorderMenuItems } from "@/api/menu";
import "@fortawesome/fontawesome-free/css/all.min.css";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import SortableTree, { getTreeFromFlatData } from "react-sortable-tree";
import "react-sortable-tree/style.css";
import { MoonLoader } from "react-spinners";

import type { CategoryType } from "@/types/category";
import type { MenuItemType } from "@/types/menu-item";

import { useMenuItems } from "@/hooks/server-state/use-menu-items";
import { useRoles } from "@/hooks/server-state/use-roles";

import { formatCategoryNames } from "@/utils/category";
import { extractCategories } from "@/utils/category";

import AlertModal from "@/components/common/modals/alert-modal";
import MenuItemModal from "@/components/common/modals/menu-item-modal";

import TableSearch, {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TablePagination,
  TableRow
} from "@/components/ui/table";

type ViewMode = "table" | "tree";

type MenuItem = {
  id: number;
  name: string;
  icon: string;
  link: string;
  order: number;
  roleId: number;
  menuType: number;
  workflowId: number | null;
  parentMenuElemntId: number | null;
  isSearchable: boolean;
  isVisible: boolean;
  nodeId?: string | undefined;
  title?: string;
  categories?: CategoryType[];
  children?: MenuItem[];
};

type CategorySummary = CategoryType & {
  itemCount: number;
};

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const getPageSize = (value: string | null) => {
  const requestedSize = Number(value);

  return PAGE_SIZE_OPTIONS.includes(requestedSize)
    ? requestedSize
    : DEFAULT_PAGE_SIZE;
};

const getCurrentPage = (value: string | null) => {
  const requestedPage = Number(value);

  return Number.isFinite(requestedPage) && requestedPage > 0
    ? requestedPage
    : 1;
};

const matchesMenuItemSearch = (item: MenuItemType, searchValue: string) => {
  const normalizedSearchValue = searchValue.trim().toLowerCase();

  if (!normalizedSearchValue) {
    return true;
  }

  return [item.name, item.link, item.icon]
    .filter(Boolean)
    .some((value) =>
      String(value).toLowerCase().includes(normalizedSearchValue)
    );
};

type State = {
  isDeleteModalOpen: boolean;
  isModalOpen: boolean;
  item: MenuItem | null;
};

enum ActionTypes {
  SET_MENU = "SET_MENU",
  SET_IS_MODAL_OPEN = "SET_IS_MODAL_OPEN",
  SET_IS_DELETE_MODAL_OPEN = "SET_IS_DELETE_MODAL_OPEN"
}

type Action =
  | {
      type: ActionTypes.SET_MENU;
      payload: MenuItem | null;
    }
  | {
      type:
        | ActionTypes.SET_IS_MODAL_OPEN
        | ActionTypes.SET_IS_DELETE_MODAL_OPEN;
      payload: boolean;
    };

/* -------------------------------------------------------------------------- */
/*                                   REDUCER                                  */
/* -------------------------------------------------------------------------- */

const initialState: State = {
  isDeleteModalOpen: false,
  isModalOpen: false,
  item: null
};

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case ActionTypes.SET_MENU:
      return {
        ...state,
        item: action.payload
      };

    case ActionTypes.SET_IS_MODAL_OPEN:
      return {
        ...state,
        isModalOpen: action.payload
      };

    case ActionTypes.SET_IS_DELETE_MODAL_OPEN:
      return {
        ...state,
        isDeleteModalOpen: action.payload
      };

    default:
      return state;
  }
}

/* -------------------------------------------------------------------------- */
/*                                   HELPERS                                  */
/* -------------------------------------------------------------------------- */

const renderIcon = (icon?: string) => {
  if (!icon) return null;

  return <i className={`text-primary ${icon}`} />;
};

const sortTree = (nodes: MenuItem[]): MenuItem[] => {
  return [...nodes]
    .sort((a, b) => a.order - b.order)
    .map((node) => ({
      ...node,
      children: node.children?.length ? sortTree(node.children) : []
    }));
};

const flattenTree = (
  nodes: MenuItem[],
  parentId: number | null = null
): {
  id: number;
  parentMenuElemntId: number | null;
  order: number;
}[] => {
  return nodes.flatMap((node, index) => {
    const current = {
      id: node.id,
      parentMenuElemntId: parentId,
      order: index
    };

    const children = node.children?.length
      ? flattenTree(node.children, node.id)
      : [];

    return [current, ...children];
  });
};

/* -------------------------------------------------------------------------- */
/*                                  COMPONENT                                 */
/* -------------------------------------------------------------------------- */

const MenuItemsPage = () => {
  const queryClient = useQueryClient();

  const [state, dispatch] = useReducer(reducer, initialState);

  const [viewMode, setViewMode] = useState<ViewMode>("table");

  const [treeData, setTreeData] = useState<MenuItem[]>([]);

  const [roleId, setRoleId] = useState<number | undefined>();

  const [searchParams, setSearchParams] = useSearchParams();

  const { data: menuItems, isLoading } = useMenuItems(
    roleId,
    viewMode,
    viewMode === "table"
      ? {
          page: 1,
          size: 50,
          search: ""
        }
      : {
          search: "",
          enabled: true
        }
  );

  const [roleSearch, setRoleSearch] = useState("");
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  const { data: roles, isLoading: isRolesLoading } = useRoles({
    search: roleSearch,
    enabled: viewMode === "tree"
  });
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const allMenuItems = (menuItems?.data ?? []) as MenuItemType[];
  const searchValue = searchParams.get("search") ?? "";
  const currentPage = getCurrentPage(searchParams.get("page"));
  const pageSize = getPageSize(searchParams.get("size"));
  const isAllItemsView = searchParams.get("view") === "all";

  const selectedCategoryIdValue = Number(searchParams.get("categoryId"));
  const selectedCategoryId =
    viewMode === "table" &&
    !isAllItemsView &&
    Number.isFinite(selectedCategoryIdValue) &&
    selectedCategoryIdValue > 0
      ? selectedCategoryIdValue
      : null;

  const categoryRows = useMemo<CategorySummary[]>(() => {
    const categoriesMap = new Map<number, CategorySummary>();

    allMenuItems.forEach((menuItem) => {
      extractCategories(menuItem).forEach((category) => {
        const existingCategory = categoriesMap.get(category.id);

        if (existingCategory) {
          existingCategory.itemCount += 1;
          return;
        }

        categoriesMap.set(category.id, {
          ...category,
          itemCount: 1
        });
      });
    });

    return Array.from(categoriesMap.values()).sort(
      (firstCategory, secondCategory) =>
        firstCategory.name.localeCompare(secondCategory.name, "fa")
    );
  }, [allMenuItems]);

  const selectedCategory = useMemo(
    () =>
      selectedCategoryId
        ? (categoryRows.find(
            (category) => category.id === selectedCategoryId
          ) ?? null)
        : null,
    [categoryRows, selectedCategoryId]
  );

  const filteredMenuItems = useMemo(() => {
    if (viewMode !== "table") {
      return [];
    }

    if (isAllItemsView) {
      return allMenuItems.filter((item) =>
        matchesMenuItemSearch(item, searchValue)
      );
    }

    if (!selectedCategory) {
      return [];
    }

    return allMenuItems.filter((item) => {
      const categoryIds = extractCategories(item).map(
        (category) => category.id
      );

      return (
        categoryIds.includes(selectedCategory.id) &&
        matchesMenuItemSearch(item, searchValue)
      );
    });
  }, [allMenuItems, isAllItemsView, searchValue, selectedCategory, viewMode]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredMenuItems.length / pageSize)
  );
  const startIndex = (currentPage - 1) * pageSize;
  const visibleMenuItems = filteredMenuItems.slice(
    startIndex,
    startIndex + pageSize
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsRoleDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  /* ------------------------------------------------------------------------ */
  /*                                  MUTATION                                */
  /* ------------------------------------------------------------------------ */

  const invalidateMenuItems = () => {
    queryClient.invalidateQueries({
      queryKey: ["menu-items"]
    });
  };

  const { mutate: deleteItem, isPending: isDeleting } = useMutation({
    mutationFn: (id: number) => deleteMenuItem(id),

    onSuccess: () => {
      invalidateMenuItems();

      dispatch({
        type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
        payload: false
      });
    }
  });

  const { mutate: reorder } = useMutation({
    mutationFn: reorderMenuItems
  });

  const { mutate: cloneItem } = useMutation({
    mutationFn: createMenuItem,
    onSuccess: invalidateMenuItems
  });

  /* ------------------------------------------------------------------------ */
  /*                                  HANDLERS                                */
  /* ------------------------------------------------------------------------ */

  const openEditModal = useCallback((item: MenuItem | null) => {
    dispatch({
      type: ActionTypes.SET_MENU,
      payload: item
    });

    dispatch({
      type: ActionTypes.SET_IS_MODAL_OPEN,
      payload: true
    });
  }, []);

  const openDeleteModal = useCallback((item: MenuItem) => {
    dispatch({
      type: ActionTypes.SET_MENU,
      payload: item
    });

    dispatch({
      type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
      payload: true
    });
  }, []);

  const handleClone = useCallback(
    (item: MenuItem) => {
      cloneItem({
        id: 0,
        menuType: item.menuType,
        name: `${item.name} (copy)`,
        link: item.link,
        parentMenuElemntId: item.parentMenuElemntId,
        roleId: item.roleId,
        workflowId: item.workflowId ?? 0,
        icon: item.icon,
        isSearchable: item.isSearchable,
        isVisible: item.isVisible,
        nodeId: item.nodeId
      });
    },
    [cloneItem]
  );

  /* ------------------------------------------------------------------------ */
  /*                                 TREE DATA                                */
  /* ------------------------------------------------------------------------ */

  const filteredItems = useMemo(() => {
    if (!menuItems?.data) return [];

    return roleId
      ? menuItems.data.filter((item) => item.roleId === roleId)
      : menuItems.data;
  }, [menuItems, roleId]);

  useEffect(() => {
    if (!filteredItems.length) {
      setTreeData([]);
      return;
    }

    const tree = getTreeFromFlatData({
      flatData: filteredItems.map((item) => ({
        ...item,
        title: item.name
      })),

      getKey: (node: MenuItem) => node.id,

      getParentKey: (node: MenuItem) => node.parentMenuElemntId,

      rootKey: null
    });

    setTreeData(sortTree(tree));
  }, [filteredItems]);

  useEffect(() => {
    if (viewMode !== "table" || (!selectedCategory && !isAllItemsView)) {
      return;
    }

    if (isAllItemsView) {
      return;
    }

    // Avoid resetting page from URL before data is ready.
    if (isLoading) {
      return;
    }

    if (currentPage > totalPages) {
      setSearchParams((prev) => {
        const params = new URLSearchParams(prev);
        params.set("page", totalPages.toString());
        return params;
      });
    }
  }, [
    currentPage,
    isAllItemsView,
    isLoading,
    selectedCategory,
    setSearchParams,
    totalPages,
    viewMode
  ]);

  const handleCategorySelect = (categoryId: number) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("categoryId", categoryId.toString());
      params.delete("view");
      params.set("page", "1");
      params.delete("size");
      params.delete("search");
      return params;
    });
  };

  const handleAllItemsSelect = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("view", "all");
      params.delete("categoryId");
      params.set("page", "1");
      params.delete("size");
      params.delete("search");
      return params;
    });
  };

  const handleBackToCategories = () => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.delete("view");
      params.delete("categoryId");
      params.delete("page");
      params.delete("size");
      params.delete("search");
      return params;
    });
  };

  /* ------------------------------------------------------------------------ */
  /*                                  LOADING                                 */
  /* ------------------------------------------------------------------------ */

  if (isLoading && viewMode === "table") {
    return <Loading />;
  }

  return (
    <>
      {/* ------------------------------------------------------------------- */}
      {/*                                MODALS                               */}
      {/* ------------------------------------------------------------------- */}

      <AlertModal
        isOpen={state.isDeleteModalOpen}
        isLoading={isDeleting}
        title="آیا از حذف اطمینان دارید؟"
        description="این عملیات قابل برگشت نیست"
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_DELETE_MODAL_OPEN,
            payload: false
          })
        }
        onConfirm={() => {
          if (!state.item?.id) return;

          deleteItem(state.item.id);
        }}
      />

      <MenuItemModal
        isOpen={state.isModalOpen}
        menuItem={state.item}
        onClose={() =>
          dispatch({
            type: ActionTypes.SET_IS_MODAL_OPEN,
            payload: false
          })
        }
      />

      {/* ------------------------------------------------------------------- */}
      {/*                                HEADER                               */}
      {/* ------------------------------------------------------------------- */}

      <div className="px-3 py-3">
        <div className="flex items-center justify-between gap-3 px-5">
          <div className="flex items-center gap-x-4">
            <ViewModeButton
              active={viewMode === "table"}
              icon={<ListIcon size={16} />}
              label="جدول"
              onClick={() => setViewMode("table")}
            />

            <ViewModeButton
              active={viewMode === "tree"}
              icon={<GitBranchIcon size={16} />}
              label="درختی"
              onClick={() => setViewMode("tree")}
            />
          </div>

          <div className="flex items-center gap-3">
            {viewMode === "tree" && (
              <button
                className="flex items-center gap-x-1 text-sm text-slate-900 transition-colors hover:text-primary"
                onClick={() => openEditModal(null)}
              >
                <i className="fa-solid fa-plus text-xs" />
                ایجاد آیتم جدید
              </button>
            )}

            {viewMode === "tree" && (
              <div>
                <div ref={dropdownRef} className="relative min-w-[260px]">
                  <button
                    type="button"
                    onClick={() => setIsRoleDropdownOpen((prev) => !prev)}
                    className="flex h-9 w-full items-center justify-between rounded border border-slate-300 bg-white px-3 text-sm"
                  >
                    <span className="truncate">
                      {roleId
                        ? roles?.data?.find((r: any) => r.id === roleId)
                            ?.nameFa || "نقش انتخاب شده"
                        : "همه نقش‌ها"}
                    </span>

                    <i className="fa-solid fa-chevron-down text-xs" />
                  </button>

                  {isRoleDropdownOpen && (
                    <div className="absolute left-0 top-full z-50 mt-1 w-full rounded border border-slate-300 bg-white shadow-lg">
                      <div className="border-b border-slate-200 p-2">
                        <input
                          autoFocus
                          type="text"
                          placeholder="جستجوی نقش..."
                          value={roleSearch}
                          onChange={(e) => setRoleSearch(e.target.value)}
                          className="h-9 w-full rounded border border-slate-300 px-2 text-sm outline-none focus:border-primary"
                        />
                      </div>

                      <div className="max-h-60 overflow-y-auto py-1">
                        <button
                          type="button"
                          onClick={() => {
                            setRoleId(undefined);
                            setIsRoleDropdownOpen(false);
                            setRoleSearch("");
                          }}
                          className={`flex w-full items-center px-3 py-2 text-right text-sm hover:bg-slate-100 ${
                            !roleId ? "bg-slate-100 font-medium" : ""
                          }`}
                        >
                          همه نقش‌ها
                        </button>

                        {isRolesLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <MoonLoader size={18} color="#0099A5" />
                          </div>
                        ) : (
                          roles?.data?.map((role: any) => (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => {
                                setRoleId(role.id);
                                setIsRoleDropdownOpen(false);
                                setRoleSearch("");
                              }}
                              className={`flex w-full items-center px-3 py-2 text-right text-sm hover:bg-slate-100 ${
                                roleId === role.id
                                  ? "bg-slate-100 font-medium"
                                  : ""
                              }`}
                            >
                              {role.nameFa}
                            </button>
                          ))
                        )}

                        {!isRolesLoading && !roles?.data?.length && (
                          <div className="px-3 py-2 text-sm text-slate-500">
                            نقشی پیدا نشد
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {viewMode === "table" && (selectedCategory || isAllItemsView) && (
          <div className="mt-3 flex flex-col gap-2 px-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-col gap-2">
              <button
                className="flex items-center gap-x-1 text-sm hover:text-primary"
                onClick={handleBackToCategories}
              >
                <ArrowLeftIcon size={14} className="text-primary" />
                بازگشت به دسته بندی ها
              </button>

              <div className="text-sm text-slate-600">
                {isAllItemsView ? (
                  <>همه آیتم ها</>
                ) : (
                  <>
                    آیتم‌های دسته بندی{" "}
                    <span className="font-semibold text-slate-900">
                      {selectedCategory?.name}
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                className="flex items-center gap-x-1 text-sm text-slate-900 transition-colors hover:text-primary"
                onClick={() => openEditModal(null)}
              >
                <i className="fa-solid fa-plus text-xs" />
                آیتم جدید
              </button>
              <TableSearch className="h-8 w-full sm:w-72" />
            </div>
          </div>
        )}
      </div>

      {/* ------------------------------------------------------------------- */}
      {/*                                 TABLE                               */}
      {/* ------------------------------------------------------------------- */}

      {viewMode === "table" && !(selectedCategory || isAllItemsView) && (
        <>
          <div className="flex items-center justify-between px-5 py-2">
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-slate-900">
                دسته بندی آیتم ها
              </p>
              <p className="mt-1 text-xs text-slate-500">
                یک دسته بندی را انتخاب کنید تا آیتم های مربوط به آن نمایش داده
                شود.
              </p>
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام دسته بندی</TableHead>
                <TableHead>تعداد آیتم</TableHead>
                <TableHead>توضیحات</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              <TableRow
                className="cursor-pointer"
                onClick={handleAllItemsSelect}
              >
                <TableCell>1</TableCell>
                <TableCell className="font-medium text-primary">همه</TableCell>
                <TableCell>{allMenuItems.length}</TableCell>
                <TableCell>نمایش همه آیتم ها</TableCell>
              </TableRow>

              {categoryRows.map((categoryItem, index) => (
                <TableRow
                  key={categoryItem.id}
                  className="cursor-pointer"
                  onClick={() => handleCategorySelect(categoryItem.id)}
                >
                  <TableCell>{index + 2}</TableCell>
                  <TableCell>{categoryItem.name}</TableCell>
                  <TableCell>{categoryItem.itemCount}</TableCell>
                  <TableCell>{categoryItem.description || "-"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!categoryRows.length && <EmptyState message="دسته بندی پیدا نشد" />}
        </>
      )}

      {viewMode === "table" && (selectedCategory || isAllItemsView) && (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ردیف</TableHead>
                <TableHead>نام</TableHead>
                <TableHead>دسته‌بندی</TableHead>
                <TableHead>نقش</TableHead>
                <TableHead>ویرایش</TableHead>
                <TableHead>کلون</TableHead>
                <TableHead>حذف</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {visibleMenuItems.map((item, index: number) => (
                <TableRow key={item.id}>
                  <TableCell>{startIndex + index + 1}</TableCell>

                  <TableCell>{item.name}</TableCell>

                  <TableCell>{formatCategoryNames(item) || "-"}</TableCell>

                  <TableCell>
                    {roles?.data?.find((r: any) => r.id === item.roleId)
                      ?.nameFa || "-"}
                  </TableCell>

                  <TableCell>
                    <button onClick={() => openEditModal(item as MenuItem)}>
                      <PencilIcon />
                    </button>
                  </TableCell>

                  <TableCell>
                    <button
                      className="hover:text-primary"
                      onClick={() => handleClone(item as MenuItem)}
                    >
                      <CopyIcon size={16} />
                    </button>
                  </TableCell>

                  <TableCell>
                    <button onClick={() => openDeleteModal(item as MenuItem)}>
                      <Trash2Icon className="text-red-500" />
                    </button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {!visibleMenuItems.length && (
            <EmptyState message="آیتمی برای این دسته بندی پیدا نشد" />
          )}

          <TablePagination totalItems={filteredMenuItems.length} />
        </>
      )}

      {/* ------------------------------------------------------------------- */}
      {/*                                  TREE                               */}
      {/* ------------------------------------------------------------------- */}

      {viewMode === "tree" && (
        <div className="px-5 pb-4">
          <div style={{ height: 700 }}>
            <SortableTree
              treeData={treeData}
              rowDirection="rtl"
              defaultCollapsed={false}
              onChange={setTreeData}
              onMoveNode={({ treeData }: any) => {
                setTreeData(treeData);

                reorder(flattenTree(treeData));
              }}
              generateNodeProps={({ node }: { node: MenuItem }) => ({
                title: (
                  <div className="flex w-full items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      {renderIcon(node.icon)}

                      <span>{node.title}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        className="hover:text-primary"
                        onClick={() => openEditModal(node)}
                      >
                        <PencilIcon size={16} />
                      </button>

                      <button
                        className="hover:text-red-500"
                        onClick={() => openDeleteModal(node)}
                      >
                        <Trash2Icon size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                )
              })}
            />
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------- */}
      {/*                                 EMPTY                               */}
      {/* ------------------------------------------------------------------- */}

      {viewMode === "tree" && !isLoading && !menuItems?.data?.length && (
        <EmptyState />
      )}
    </>
  );
};

/* -------------------------------------------------------------------------- */
/*                              SHARED COMPONENTS                             */
/* -------------------------------------------------------------------------- */

type ViewModeButtonProps = {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
};

const ViewModeButton = ({
  active,
  label,
  icon,
  onClick
}: ViewModeButtonProps) => {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-x-2 rounded px-3 py-1 text-sm ${
        active ? "bg-slate-200" : ""
      }`}
    >
      {icon}
      {label}
    </button>
  );
};

const Loading = () => (
  <div className="flex h-[50vh] items-center justify-center">
    <MoonLoader color="#0099A5" size={50} />
  </div>
);

const EmptyState = ({
  message = "داده‌ای وجود ندارد"
}: {
  message?: string;
}) => (
  <div className="flex h-32 items-center justify-center bg-white shadow-md">
    <p className="text-slate-500">{message}</p>
  </div>
);

export default MenuItemsPage;
